import { ArrowUpRightFromSquare, Button, Paragraph, PlusIcon, PrescriptionIcon } from '@quickmd-llc/design-system'
import React, { useCallback, useState } from 'react'
import {
  getVisitsVisitIdEncounterUrl,
  getVisitsVisitIdTreaturl,
  useGetVisitsVisitIdPrescriptions,
} from 'api/generated/prescription/prescription'
import { type EncounterUrlDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { windowOpenArgs } from 'utils/windowOpenArgs'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { useDispatch } from 'react-redux'
import { getTabId } from 'utils/common'
import { OrderCategories, ServiceTypeCategory } from 'components/product-order-popup/common'
import { useGetVisitsVisitIdV2 } from 'api/generated/visit-controller-v2/visit-controller-v2'
import { PrescriptionTile, type PrescriptionTileData } from './PrescriptionTile'
import { isPendingAuthorization } from './prescriptionStatus'
import { useSelector } from 'store/hooks/useSelector'
import { selectDtsMoudPilotEnabled } from 'store/flags/FlagsSelectors'
import { useDawShowTreatAndAdd } from 'hooks/useDawShowTreatAndAdd'
import { getProducts } from 'api/generated/product/product'
import { GET_PRODUCTS_ERROR, GET_PRODUCTS_SUCCESS } from 'store/products/productsConstants'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { useIsProviderScriptSureCohort } from 'hooks/useIsProviderScriptSureCohort'
import { type RequestedPharmacy } from '../requestedPharmacy'
import { PatientConfirmedPrescription } from './PatientConfirmedPrescription'
import { RequestedPharmacyCard } from './RequestedPharmacyCard'

type Props = {
  setIsLoading?: (isLoading: boolean) => void
  /** The pharmacy the patient asked for (`useRequestedPharmacy`); `null` hides the card. */
  requestedPharmacy: RequestedPharmacy | null
}
export const VisitOrdersView = ({ setIsLoading, requestedPharmacy }: Props) => {
  const { patientWithVisitInfo } = useDailyVisit()
  const dispatch = useDispatch()
  const isProviderScriptSure = useIsProviderScriptSureCohort()
  const dawShowTreatAndAdd = useDawShowTreatAndAdd()
  const showBothTreatAndAdd = isProviderScriptSure && dawShowTreatAndAdd

  const flags = useSelector((state) => state.flags.flags)
  const dtsMoudPilotEnabled = useSelector(selectDtsMoudPilotEnabled)
  const [retrievingTreatUrl, setRetrievingTreatUrl] = useState(false)
  const [patientTreatLink, setPatientTreatLink] = useState<string | null>(null)
  const [retrievingEncounterUrl, setRetrievingEncounterUrl] = useState(false)
  const [patientEncounterLink, setPatientEncounterLink] = useState<string | null>(null)
  const { data: prescriptions } = useGetVisitsVisitIdPrescriptions(patientWithVisitInfo?.VisitId || '', {
    query: { enabled: false },
  })
  const authorizationNeededPrescriptions = prescriptions?.filter(isPendingAuthorization) || []
  const orderedPrescriptions = prescriptions?.filter((p) => !isPendingAuthorization(p)) || []

  const { data: visitDetails } = useGetVisitsVisitIdV2(
    patientWithVisitInfo?.VisitId || '',
    { includeMostRecentMatchingVisit: true },
    { query: { enabled: false } },
  )
  const showDTSButton = React.useMemo(
    () =>
      (flags.product_order_types?.includes('OUD') &&
        dtsMoudPilotEnabled &&
        visitDetails?.ServiceTypeCategory === ServiceTypeCategory.MedicationAssistedTreatment) ||
      (flags.product_order_types?.includes('MHT') &&
        visitDetails?.ServiceTypeCategory === ServiceTypeCategory.MenopauseHormoneTherapy),
    [dtsMoudPilotEnabled, flags.product_order_types, visitDetails?.ServiceTypeCategory],
  )

  const handleAddOrder = useCallback(() => {
    const { PatientId, VisitSummary } = patientWithVisitInfo || {}
    if (!PatientId) return
    const tabId = getTabId() || ''
    const category =
      visitDetails?.ServiceTypeCategory === ServiceTypeCategory.MedicationAssistedTreatment ?
        OrderCategories.moud
      : OrderCategories.mht

    const eventType =
      category === OrderCategories.moud ?
        PROVIDER_EVENT_NAMES.LAUNCH_DTS_FOR_OUD
      : PROVIDER_EVENT_NAMES.LAUNCH_DTS_FOR_MHT
    tracking.trackEvent(eventType, {
      Location: 'In-Progress Visit',
      PatientId,
      PatientPastVisitCount: VisitSummary?.CountPast || 0,
      PatientUpcomingVisitCount: VisitSummary?.CountUpcoming || 0,
    } as any)

    setIsLoading?.(true)
    getProducts({ Category: category, PatientId, StateCode: visitDetails?.StateCode ?? '' })
      .then((response) => {
        dispatch({
          type: GET_PRODUCTS_SUCCESS,
          payload: response,
          extraData: {
            patientId: PatientId,
            tabId,
            usState: visitDetails?.StateCode,
            category,
          },
        })
      })
      .catch((error) => {
        dispatch({
          type: GET_PRODUCTS_ERROR,
          payload: error,
          extraData: {
            patientId: PatientId,
            tabId,
            usState: visitDetails?.StateCode,
            category,
          },
        })
      })
      .finally(() => {
        setIsLoading?.(false)
        setTimeout(() => {
          document.getElementById('root')?.removeAttribute('inert')
        }, 500)
      })
  }, [patientWithVisitInfo, visitDetails?.ServiceTypeCategory, visitDetails?.StateCode, setIsLoading, dispatch])
  const handleOpenTreat = useCallback(async () => {
    if (retrievingTreatUrl) return
    const { PatientId, VisitSummary, VisitId } = patientWithVisitInfo || {}
    tracking.trackEvent(PROVIDER_EVENT_NAMES.LAUNCH_TREAT, {
      Location: 'In-Progress Visit',
      PatientId,
      PatientPastVisitCount: VisitSummary?.CountPast || 0,
      PatientUpcomingVisitCount: VisitSummary?.CountUpcoming || 0,
    } as any)
    if (patientTreatLink) {
      window.open(patientTreatLink, ...windowOpenArgs)
      return
    }
    if (!VisitId) return
    try {
      setRetrievingTreatUrl(true)
      const response: EncounterUrlDoctorApiOutContract = await getVisitsVisitIdTreaturl(VisitId)
      const url = response?.ProfileUrl ?? null
      if (url) {
        setPatientTreatLink(url)
        window.open(url, ...windowOpenArgs)
      }
    } catch (error) {
      console.error('Failed to retrieve Treat profile URL', error)
    } finally {
      setRetrievingTreatUrl(false)
    }
  }, [retrievingTreatUrl, patientWithVisitInfo, patientTreatLink])

  const handleOpenEncounter = useCallback(async () => {
    if (retrievingEncounterUrl) return
    const { PatientId, VisitSummary, VisitId } = patientWithVisitInfo || {}
    tracking.trackEvent(PROVIDER_EVENT_NAMES.LAUNCH_DAW, {
      Location: 'In-Progress Visit',
      PatientId,
      PatientPastVisitCount: VisitSummary?.CountPast || 0,
      PatientUpcomingVisitCount: VisitSummary?.CountUpcoming || 0,
    } as any)
    if (patientEncounterLink) {
      window.open(patientEncounterLink, ...windowOpenArgs)
      return
    }
    if (!VisitId) return
    try {
      setRetrievingEncounterUrl(true)
      const response: EncounterUrlDoctorApiOutContract = await getVisitsVisitIdEncounterUrl(VisitId)
      const url = response?.ProfileUrl ?? null
      if (url) {
        setPatientEncounterLink(url)
        window.open(url, ...windowOpenArgs)
      }
    } catch (error) {
      console.error('Failed to retrieve encounter URL', error)
    } finally {
      setRetrievingEncounterUrl(false)
    }
  }, [retrievingEncounterUrl, patientWithVisitInfo, patientEncounterLink])

  const onOpenEncounter = useCallback((prescription: PrescriptionTileData) => {
    if (prescription.EncounterUrl) {
      window.open(prescription.EncounterUrl, '_blank', 'noopener,noreferrer')
    }
  }, [])

  return (
    <div className='flex w-full flex-col gap-sp-2 bg-white p-sp-2'>
      <div className='flex h-sp-4 justify-between gap-sp-1'>
        <div className='flex items-center gap-sp-0.5'>
          <PrescriptionIcon />
          <Paragraph className='font-semibold text-text-dark'>Orders</Paragraph>
        </div>
        <div className='flex gap-sp-1'>
          {showBothTreatAndAdd ?
            <>
              <Button size='s' priority='secondary' onClick={handleOpenEncounter} disabled={retrievingEncounterUrl}>
                <PlusIcon /> Add
              </Button>
              {showDTSButton ?
                <Button
                  size='s'
                  priority='secondary'
                  onClick={() => {
                    handleAddOrder()
                  }}
                  disabled={retrievingTreatUrl}>
                  <PlusIcon /> Add
                </Button>
              : null}
              <Button size='s' priority='secondary' onClick={handleOpenTreat} disabled={retrievingTreatUrl}>
                Treat <ArrowUpRightFromSquare />
              </Button>
            </>
          : isProviderScriptSure ?
            <Button size='s' priority='secondary' onClick={handleOpenEncounter} disabled={retrievingEncounterUrl}>
              <PlusIcon /> Add
            </Button>
          : <>
              {showDTSButton ?
                <Button
                  size='s'
                  priority='secondary'
                  onClick={() => {
                    handleAddOrder()
                  }}
                  disabled={retrievingTreatUrl}>
                  <PlusIcon /> Add
                </Button>
              : null}
              <Button size='s' priority='secondary' onClick={handleOpenTreat} disabled={retrievingTreatUrl}>
                Treat <ArrowUpRightFromSquare />
              </Button>
            </>
          }
        </div>
      </div>
      <PatientConfirmedPrescription
        visitMethod={patientWithVisitInfo?.VisitMethod}
        preference={visitDetails?.PatientPreference}
      />
      <RequestedPharmacyCard
        visitId={patientWithVisitInfo?.VisitId}
        patientId={patientWithVisitInfo?.PatientId}
        requested={requestedPharmacy}
        prescriptions={prescriptions}
      />
      {orderedPrescriptions && orderedPrescriptions.length > 0 ?
        <div className='space-y-sp-1'>
          {orderedPrescriptions.map((prescription) => (
            <PrescriptionTile
              key={prescription.PrescriptionId}
              prescription={prescription}
              handleClick={onOpenEncounter}
            />
          ))}
        </div>
      : null}
      {orderedPrescriptions.length === 0 && authorizationNeededPrescriptions.length === 0 && (
        <div className='flex min-h-[160px] items-center justify-center rounded-lg border border-border-medium px-sp-2 py-sp-3'>
          <Paragraph size='sm' className='text-text-light'>
            No orders yet.
          </Paragraph>
        </div>
      )}
      {authorizationNeededPrescriptions && authorizationNeededPrescriptions.length > 0 ?
        <div className='flex flex-col gap-sp-1'>
          <Paragraph className='font-semibold text-text-light' size='sm'>
            Pending authorization in Treat
          </Paragraph>
          {authorizationNeededPrescriptions.map((prescription) => (
            <PrescriptionTile
              key={prescription.PrescriptionId}
              prescription={prescription}
              handleClick={onOpenEncounter}
            />
          ))}{' '}
        </div>
      : null}
    </div>
  )
}
